import axios, { AxiosInstance } from 'axios';

import { convertToHeaderContent } from './contentConverter';
import { serializeBaseUrl, serializeConfigurationOptions } from './queryUtils';
import {
    CorrelationId,
    HeaderConfigurationOptions,
    HeaderContentImpl,
    HeaderLocaleOptions,
    HeaderServiceError,
    HeaderServiceResponse,
    HeaderContent,
} from './types';

export interface HeaderServiceConfiguration {
    environment: 'prod' | 'dev' | 'beta' | 'release';
    domain: string;
    locale: string;
    serverLogFn?: (error: any) => void;
    correlationId?: CorrelationId;
    showCart?: boolean;
    showCategories?: boolean;
    showMultilanguage?: boolean;
    showSearch?: boolean;
    searchFlyoutOpen?: boolean;
    highlightPath?: string;
    isNextJS?: boolean;
    axiosInstance?: AxiosInstance;
    enableDiscoverBackend?: boolean;
}

export async function fetchHeaderContent({
    environment,
    domain,
    locale,
    showCart = true,
    showCategories = true,
    showMultilanguage = true,
    showSearch = true,
    searchFlyoutOpen = true,
    correlationId,
    // eslint-disable-next-line no-console
    serverLogFn = console.error,
    isNextJS = true,
    highlightPath,
    axiosInstance = axios,
    enableDiscoverBackend = false,
}: HeaderServiceConfiguration): Promise<HeaderContent> {
    const configurationOptions: HeaderConfigurationOptions = {
        showCart,
        showCategories,
        showMultilanguage,
        showSearch,
        searchFlyoutOpen,
        highlightPath: highlightPath ?? null,
    };

    const localeOptions: HeaderLocaleOptions = { locale, domain };
    const baseUrl = serializeBaseUrl(
        environment,
        localeOptions,
        enableDiscoverBackend,
    );
    const serializedOptions =
        serializeConfigurationOptions(configurationOptions);

    const queryUrl = `${baseUrl}?${serializedOptions}`;
    const callerMaybe = process.env.APPLICATION_NAME;

    try {
        const result = await axiosInstance.get<HeaderServiceResponse>(
            queryUrl,
            {
                headers: {
                    ...(callerMaybe && {
                        'x-caller': callerMaybe,
                    }),
                    ...(correlationId && {
                        'x-correlation-id': correlationId,
                    }),
                },
            },
        );

        return convertToHeaderContent({
            headerServiceResponse: result.data,
            highlightPath,
            isNextJS,
        });
    } catch (error) {
        if (typeof window === 'undefined') {
            serverLogFn(error);
        }

        return { isError: true };
    }
}

export function isHeaderServiceError(
    value?: HeaderContent,
): value is HeaderServiceError {
    return (value as HeaderServiceError)?.isError === true;
}

export function isHeaderContent(
    value?: HeaderContent,
): value is HeaderContentImpl {
    return (value as HeaderContentImpl)?.domain !== undefined;
}
